import { apiFetch } from '@/api/apiFetch';
import { DTOToTemplate } from '@/api/templateMapper';
import type { ITemplate } from '@/domain/template/ITemplate';
import type { ITemplateDTO } from '@/domain/template/ITemplateDTO';

interface ITemplateApiResponse {
    id: string;
    name: string;
    productId: string;
    projectJson: Record<string, unknown>;
    thumbnail: string | null;
}

interface ITemplateFileApiResponse {
    id: string;
    name: string;
    type: string;
}

function mapResponseToTemplate(data: ITemplateApiResponse): ITemplate {
    return DTOToTemplate({
        id: data.id,
        name: data.name,
        projectJson: data.projectJson,
        thumbnail: data.thumbnail ?? undefined,
    });
}

async function getTemplate(productName: string, templateName: string): Promise<ITemplate> {
    const response = await apiFetch(`/templates/${productName}/${templateName}`);

    if (response.status === 404) {
        throw new Error('Template not found');
    }

    if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.status}`);
    }

    const data: ITemplateApiResponse = await response.json();
    return mapResponseToTemplate(data);
}

async function postTemplate(productName: string, dto: ITemplateDTO): Promise<ITemplate> {
    const response = await apiFetch(`/templates/${productName}`, { method: 'POST', body: JSON.stringify(dto) }, [
        ['Content-Type', 'application/json'],
    ]);

    if (!response.ok) {
        throw new Error(`Failed to create template: ${response.status}`);
    }

    const data: ITemplateApiResponse = await response.json();
    return mapResponseToTemplate(data);
}

async function patchTemplate(productName: string, templateName: string, dto: Partial<ITemplateDTO>): Promise<ITemplate> {
    const response = await apiFetch(`/templates/${productName}/${templateName}`, { method: 'PATCH', body: JSON.stringify(dto) }, [
        ['Content-Type', 'application/json'],
    ]);

    if (!response.ok) {
        throw new Error(`Failed to update template: ${response.status}`);
    }

    const data: ITemplateApiResponse = await response.json();
    return mapResponseToTemplate(data);
}

async function getTemplateFile(fileId: string): Promise<Blob> {
    const response = await apiFetch(`/template-files/${fileId}`);

    if (!response.ok) {
        throw new Error(`Failed to fetch template file: ${response.status}`);
    }

    return response.blob();
}

async function postTemplateFile(blob: Blob): Promise<ITemplateFileApiResponse> {
    const formData = new FormData();
    formData.append('file', blob, 'thumbnail.jpg');

    const response = await apiFetch('/template-files', {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        throw new Error(`Failed to upload thumbnail: ${response.status}`);
    }

    return response.json();
}

async function getTemplates(productName: string): Promise<{ name: string; thumbnail: string | null }[]> {
    const response = await apiFetch(`/templates/${productName}`);
    if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.status}`);
    }
    const data: ITemplateApiResponse[] = await response.json();
    return data.map((item) => ({ name: item.name, thumbnail: item.thumbnail }));
}

async function deleteTemplate(productName: string, templateName: string): Promise<void> {
    const response = await apiFetch(`/templates/${productName}/${templateName}`, { method: 'DELETE' });
    if (!response.ok) {
        throw new Error(`Failed to delete template: ${response.status}`);
    }
}

export { deleteTemplate, getTemplate, getTemplateFile, getTemplates, patchTemplate, postTemplate, postTemplateFile };
